import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from './index';
import { UserService } from '../services/UserService';
import { setModalSignInUp, wipeLocalStorage } from './AppSlice';
import { modalTypeEnum } from '../types/modal';
import { GeneratedLink } from '../types/user';
import { setNotification } from './NotificationSlice';

const initialState = {
    loaderStatus: false,
    userInfo: {},
    wallet: '',
    userSettings: {},
    savedPassword: '',
    userFee: 4,
    nposUnitFee: 0,
    nposActiveNposList: []
};

const UserSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUserInfo: (state, action: PayloadAction<any>) => {
            state.userInfo = action.payload;
            if(action.payload?.wallet?.hash_address) localStorage.setItem('wallet',action.payload.wallet.hash_address)
        },
        setUserWallet: (state, action: PayloadAction<any>) => {
            state.wallet = action.payload;
        },
        setUserSettings: (state, action: PayloadAction<any>) => {
            state.userSettings = action.payload;
        },
        setSavedpassword:(state, action: PayloadAction<string>) => {
            state.savedPassword = action.payload;
        },
        setUserFee:(state, action: PayloadAction<number>) => {
            state.userFee = action.payload;
        },
        setNposUnitFee:(state, action: PayloadAction<number>) => {
            state.nposUnitFee = action.payload;
        },
        setActiveNposList:(state, action: PayloadAction<any>) => {
            state.nposActiveNposList = action.payload;
        },
    }
});

// default list exports
export const { setUserInfo, setUserWallet, setUserSettings, setSavedpassword, setUserFee, setNposUnitFee,setActiveNposList } = UserSlice.actions

// export consts
// list
export const getUserProfile = (isWithdraw?:boolean) : AppThunk<Promise<void>> => async (dispatch,getState) => {

    const info : any = await UserService.getMeInfo();
    if(info) {
        dispatch(setUserInfo(info.data));
        if (!isWithdraw) {
            dispatch(setModalSignInUp(modalTypeEnum.none));
        }
    } else {
        if (checkStorage()) dispatch(setModalSignInUp(modalTypeEnum.deviceLogin));
        else dispatch(setModalSignInUp(modalTypeEnum.unauthorized));
    }

}
export const getUserInfo = (state: RootState) => state.user.userInfo
export const getUserNposList = (state: RootState) => state.user.nposActiveNposList
export const getUserwallet = (state: RootState) => state.user.wallet
export const getSettedUserSetting = (state: RootState) => state.user.userSettings
export const getUserFee = (state: RootState) => state.user.userFee

// Change User settings
export const requestUserSettings = (): AppThunk<Promise<any>> => async (dispatch, getState) => {
    
    try {
        const userSettings = await UserService.getUserSettings();
        if (userSettings?.data) {
            dispatch(setUserFee(userSettings.data.fee))
            dispatch(setUserSettings({slippage: userSettings.data.slippage, execute_charge: userSettings.data.execute_charge}))
            return false;
        }
        return userSettings;
    } catch (err) {
        console.error(err);
    }
};

export const changeUserSettings = (slippage_value? : number, execute_charge_value? : string) : AppThunk<Promise<void>> => async (dispatch,getState) => {
    
    const settings: any = await UserService.getUserSettings();
    if (!settings?.data) {
        dispatch(setNotification({
            status: true,
            title: "Erro ao obter configurações",
            msg: "Não foi possível carregar as configurações do usuário.",
            class: "error",
            position: "top"
        }));
        return;
    }

    let updateSlippage = slippage_value !== undefined;
    let updateExecuteCharge = execute_charge_value !== undefined;

    const payload = {
        slippage : settings.data.slippage,
        execute_charge : settings.data.execute_charge_value,
        ...(updateSlippage && { slippage: slippage_value }),
        ...(updateExecuteCharge && { execute_charge: execute_charge_value })
    };

    // Update
    const changeResponse: any = await UserService.changeUserSettings(payload);

    if (changeResponse?.error) {
        dispatch(setNotification({
            status: true,
            title: "Erro na atualização",
            msg: `Erro com a atualização - err: ${changeResponse.error.response?.status || 'desconhecido'}`,
            class: 'error',
            position: 'top'
        }));
        return;
    }

    dispatch(setUserSettings(payload));
    const successMessage = updateSlippage
        ? "Slippage atualizado com sucesso"
        : updateExecuteCharge
        ? "Auto Execução atualizado com sucesso"
        : "Configuração atualizada com sucesso";

    dispatch(setNotification({
        status: true,
        title: "Configurações atualizadas",
        msg: successMessage,
        class: "success",
        position: "top"
    }));

}

// NPOS
export const generateNposLink = (name:string, fee:string): AppThunk<Promise<GeneratedLink | undefined>> => async (dispatch, getState)  => {
    try {
        const generatedLink      =  await UserService.generateLink(name, Number(fee));
        return new Promise( resolve => resolve(generatedLink));
    } catch (err) {
        console.error (err);
    }
}

export const getNposList = (): AppThunk<void> => async (dispatch, getState)  => {
    try {
        const listNpos = await UserService.getNposList();
        dispatch( setActiveNposList(listNpos.npos_name) );
    } catch (err) {
        console.error (err);
    }
}

export const getNposProfile = (isWithdraw?:boolean) : AppThunk<Promise<void>> => async (dispatch,getState) => {

    const info : any = await UserService.getNposInfo();
    if(info) {
        dispatch(setUserInfo(info.data));
        if (!isWithdraw) {
            dispatch(setModalSignInUp(modalTypeEnum.none));
        }
    } else {
        if (checkStorage()) dispatch(setModalSignInUp(modalTypeEnum.deviceLogin));
        else dispatch(setModalSignInUp(modalTypeEnum.unauthorized));
    }

}
export const requestNposSettings = (): AppThunk<Promise<any>> => async (dispatch, getState) => {
    
    try {
        const userSettings : any = await UserService.getNposSettings();
        console.dir(userSettings);
        if (userSettings?.data) {
            dispatch(setUserFee(userSettings.data.fee))
            dispatch(setUserSettings({slippage: userSettings.data.slippage, execute_charge: userSettings.data.execute_charge}))
            if (userSettings?.data?.npos_unit_fee) {
                dispatch(setNposUnitFee(userSettings.data.npos_unit_fee))
            }
            return false;
        }
        return userSettings;
    } catch (err) {
        console.error(err);
    }
};

// LOGOFF
export const logoff = () : AppThunk<void> => async (dispatch) => {
    // resetando userInfo
    dispatch(setUserInfo({}));
    dispatch(setUserWallet(''));
    // apagando token locais
    dispatch(wipeLocalStorage()) 
}

export const checkStorage = () => {
    return localStorage.getItem('encryptRt') && localStorage.getItem('encryptAt');
}

// EXPORT DEFAULT
export default UserSlice.reducer;